// Datos de la carga que viven solo en el celular: lo escaneado con la cámara y lo que no subió al confirmar.
// Van a Dexie y no a la memoria de la pantalla: si se bloquea el celular o Android cierra la pestaña para liberar
// memoria, al volver sigue todo donde estaba.
import { uuidv7 } from '@reparto/shared/nucleo';
import { type CargaConfirmada, db, type LecturaCodigo } from './db';
import { useVivo } from './vivo';

export const claveCarga = (movilId: string, fecha: string) => `${movilId}|${fecha}`;

/** Lo escaneado de esta carga, reactivo: la lista consolidada se actualiza sola con cada lectura. */
export function useLecturas(movilId: string | undefined, fecha: string | undefined): LecturaCodigo[] {
  const clave = movilId && fecha ? claveCarga(movilId, fecha) : '';
  return useVivo(
    async () => (clave ? ((await db.escaneos.get(clave))?.lecturas ?? []) : []),
    [clave],
    [] as LecturaCodigo[],
  );
}

export async function agregarLecturas(
  movilId: string,
  fecha: string,
  nuevas: Array<Omit<LecturaCodigo, 'id' | 'hora'>>,
): Promise<void> {
  if (!nuevas.length) return;
  const id = claveCarga(movilId, fecha);
  const hora = new Date().toISOString();
  await db.transaction('rw', db.escaneos, async () => {
    const previo = await db.escaneos.get(id);
    await db.escaneos.put({
      id,
      movil_id: movilId,
      fecha,
      lecturas: [...(previo?.lecturas ?? []), ...nuevas.map((n) => ({ ...n, id: uuidv7(), hora }))],
      actualizado_en: hora,
    });
  });
}

/** Saca la última lectura (para corregir una caja contada dos veces). Devuelve la que sacó. */
export async function deshacerLectura(movilId: string, fecha: string): Promise<LecturaCodigo | null> {
  const id = claveCarga(movilId, fecha);
  return db.transaction('rw', db.escaneos, async () => {
    const previo = await db.escaneos.get(id);
    const ultima = previo?.lecturas.at(-1) ?? null;
    if (previo && ultima) {
      await db.escaneos.put({
        ...previo,
        lecturas: previo.lecturas.slice(0, -1),
        actualizado_en: new Date().toISOString(),
      });
    }
    return ultima;
  });
}

export const borrarLecturas = (movilId: string, fecha: string) => db.escaneos.delete(claveCarga(movilId, fecha));

/** Se queda solo con lo escaneado de la carga actual: lo de otros días o móviles ya no sirve. */
export const limpiarLecturasViejas = (movilId: string, fecha: string) =>
  db.escaneos.where('id').notEqual(claveCarga(movilId, fecha)).delete();

export async function guardarCargaConfirmada(c: Omit<CargaConfirmada, 'id'>): Promise<void> {
  const id = claveCarga(c.movil_id, c.fecha);
  await db.transaction('rw', db.cargas, async () => {
    await db.cargas.where('id').notEqual(id).delete();
    await db.cargas.put({ ...c, id });
  });
}

export function useCargaConfirmada(movilId: string | undefined, fecha: string | undefined): CargaConfirmada | null {
  const clave = movilId && fecha ? claveCarga(movilId, fecha) : '';
  return useVivo(async () => (clave ? ((await db.cargas.get(clave)) ?? null) : null), [clave], null);
}
